import * as React from "react";
import { useContext } from "react";
import { RouteContext } from '../../providers';
import { OnlineplatTextComponent, BackIcon, CrossIcon } from "../icons";
import * as ReactDOM from "react-dom";
import { colors, fonts, fontSizes, fontWeights, headerSettings, headerZIndex } from "../../style";
import { css, footerNode, headerNode } from "../../shared-exports";
import { Icon } from "../ui";
import { useScrollHide } from "../utils/useScrollHide";
import { Tabs } from '../ui/tabs';
import useWindowSize from '../utils/windowSize';
import '../../style/mainHeader.css';

export const HeaderPortal: React.FC<{ children?: React.ReactNode }> = (props) => {
	const element = document.getElementById(headerNode);
	if (element == null) {
		return null
	};
	return ReactDOM.createPortal(props.children, element);
}

export const FooterPortal: React.FC<{ children?: React.ReactNode }> = (props) => {
	const element = document.getElementById(footerNode);
	if (element == null) {
		return null
	};
	return ReactDOM.createPortal(props.children, element);
}
export const HeaderBarSettings = headerSettings;

const backButtonHeight = headerSettings.height - headerSettings.dividerHeight;

export const HeaderBarCSS = {
	backButtonStyle: {
		position: 'absolute' as const,
		left: 0,
		top: headerSettings.dividerHeight,
		width: headerSettings.backButtonWidth,
		height: backButtonHeight,
		zIndex: headerZIndex.backButton,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		transition: 'all 100ms ease-in-out',
	},
	containerStyle: {
		position: 'relative' as const,
		width: '100%',
		height: headerSettings.height,
		zIndex: headerZIndex.container,
		padding: 0,
		overflow: 'hidden',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		borderBottom: '1px solid var(--color-border)',
		borderRadius: '0 0 6px 6px',
		boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
	} as React.CSSProperties,
	overlayBackIcon: 'text-black text-sm',
	contents: 'flex flex-col items-center justify-center h-full p-0',
	title: 'flex-1 grow-0 font-normal text-lg min-h-[34px] leading-[34px] text-center flex flex-col justify-center items-center relative !w-full tracking-[1.08px]',
	title1: 'w-full flex-1 grow-0 font-normal text-lg min-h-[34px] leading-[34px] text-center flex flex-col justify-center items-center absolute top-[5px] left-1/2 -translate-x-1/2 translate-y-1/2 tracking-[1.08px]',
	subtitle: 'flex-1 grow-0 font-light -mt-[5px] pb-[5px] text-sm',
	banner: '[&_svg]:w-[140px] [&_svg]:h-[28px] text-black'
};

export const HeaderBar: React.FC<{
	title?: string
	titleOnHold?: () => any
	onClickTitle?: () => any
	subtitle?: string
	style?: React.CSSProperties
	backButton?: () => any
	hideBackButtonOnScroll?: boolean
	hideBackButton?: boolean
	SettingModal?: () => void;
	isBackButtonClose?: boolean
	showTabs?: boolean
	router?: any
	path?: any
	children?: React.ReactNode
}> = (props) => {
	const router = useContext(RouteContext);
	const hidden = useScrollHide(20);
	const { isDesktop } = useWindowSize();

	const { isBackButtonClose, onClickTitle, showTabs } = props;

	const gotoGalleryPage = () => {
		router.push("/product/gallery", "none");
	};

	// Desktop layout with tabs (replaces MainHeader)
	if (showTabs && isDesktop) {
		return (
			<div className="mainHeader-Container">
				<div style={{ display: 'flex', alignItems: 'center', position: 'relative', width: '100%' }}>
					<div style={{ flex: '1 1 33%' }}>
						{props.backButton && (
							<Icon
								className="searchBarIcon"
								component={isBackButtonClose ? CrossIcon : BackIcon}
								aria-label={isBackButtonClose ? "Stäng" : "Tillbaka"}
								onClick={(e: any) => { e.stopPropagation(); props.backButton!(); }}
							/>
						)}
					</div>
					<div style={{ flex: '1 1 33%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<div style={{ justifyContent: 'center', alignItems: 'center', fontWeight: fontWeights.normal, fontSize: fontSizes.xl, color: colors.foreground, textAlign: 'center' }}>
							{props.title ? props.title : <Icon component={OnlineplatTextComponent} className="main-logo" aria-label="Onlineplåt – till startsidan" onClick={gotoGalleryPage} />}
							{props.subtitle && <p className="subtitle">{props.subtitle}</p>}
						</div>
					</div>
					<div style={{ flex: '1 1 33%', display: 'flex', justifyContent: 'flex-end' }}>
						<nav className="Tabs-container" aria-label="Huvudnavigering">
							<Tabs />
						</nav>
					</div>
					{(window.location.pathname.includes("/app/project/cart") || window.location.pathname.includes("/app/project/unsorted")) && props.SettingModal ?
						<Icon type="setting"
							aria-label="Inställningar"
							style={{ position: 'absolute', right: 11, top: 14, fontSize: fontSizes.xl, cursor: 'pointer' }}
							onClick={(e: any) => { e.stopPropagation(); props.SettingModal!(); }}
						/>
						: null}
				</div>
			</div>
		);
	}

	// Mobile / standard layout
	return (
		<>
			<div style={{ ...HeaderBarCSS.containerStyle, lineHeight: 1.0, background: colors.background, ...props.style }}>
				<div style={{ height: HeaderBarSettings.dividerHeight }} />
				{props.backButton && (
					<div style={HeaderBarCSS.backButtonStyle}
						onClick={(e) => { e.stopPropagation(); props.backButton!() }}>
						<Icon
							component={isBackButtonClose ? CrossIcon : BackIcon}
							aria-label={isBackButtonClose ? "Stäng" : "Tillbaka"}
							style={{ fontSize: fontSizes.sm, color: colors.black }}
						/>
					</div>
				)}
				<div className={HeaderBarCSS.contents} onClick={() => onClickTitle && onClickTitle()}>
					{!props.children && <>
						<div className={HeaderBarCSS.title} style={{ color: colors.highlight, fontFamily: fonts.normal }}>
							{props.title ? <div style={{
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								minWidth: '75vw',
								maxWidth: '75vw',
								fontWeight: fontWeights.normal,
								marginTop: props.subtitle === undefined || props.subtitle === "" ? 0 : "-10px"
							}}>{props.title}
							</div> :
								<>
									<Icon component={OnlineplatTextComponent} className={HeaderBarCSS.banner} /></>
							}
						</div>
						{props.subtitle && <span className={HeaderBarCSS.subtitle} style={{ color: colors.normal, fontFamily: fonts.normal, marginTop: props.title ? "-10px" : undefined }}>{props.subtitle}</span>}
						{(window.location.pathname.includes("/app/project/cart") || window.location.pathname.includes("/app/project/unsorted")) && props.SettingModal ?
							<Icon type="setting"
								aria-label="Inställningar"
								style={{ position: 'absolute', right: 12, top: HeaderBarSettings.dividerHeight + 12, fontSize: fontSizes.xl, cursor: 'pointer', zIndex: 10 }}
								onClick={(e: any) => { e.stopPropagation(); props.SettingModal!(); }}
							/>
							: null}
					</>
					}
					{props.children}
				</div>
			</div>
		</>
	);
}

interface FooterBarProps {
	style?: React.CSSProperties
	children?: React.ReactNode
}
export const FooterBar: React.FC<FooterBarProps> = (props) => {
	const { style: styleProp = {} } = props;
	return (
		<div className={css.AppFooter} style={styleProp}>
			{props.children}
		</div>
	);
}
